import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import {
  EconomySettings,
  STARTING_SITUATIONS,
  formatPence,
  type EconomySettings as Settings,
  type StartingSituation,
} from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { DataTable } from '@london-life/ui/admin/data-table';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { StatCard } from '@london-life/ui/admin/stat-card';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../../lib/api';
import { useAdmin } from '../../lib/admin-context';
import { parsePounds, poundsText } from '../../lib/money';
import { useRequest } from '../../lib/use-request';

type LimitKey = Exclude<keyof Settings, 'startingMoney' | 'newAccountHoldDays' | 'transferFeeBps'>;
const LIMITS: { key: LimitKey; label: string; hint: string }[] = [
  { key: 'perTransferLimitPence', label: 'Largest single transfer', hint: 'Between players: rent, bills or gifts.' },
  { key: 'dailyTransferLimitPence', label: 'Transfers per day', hint: 'The most a player can send in 24 hours.' },
  {
    key: 'transferReviewAbovePence',
    label: 'Review transfers above',
    hint: 'Larger transfers wait in the review queue (Money → Transfers).',
  },
  {
    key: 'transferFeeMinPence',
    label: 'Smallest fee on a transfer',
    hint: 'The sender pays the fee on top of what they send. It goes to the system’s fees account.',
  },
  {
    key: 'approvalThresholdPence',
    label: 'Second admin needed above',
    hint: 'Corrections and reversals above this wait for another admin. Only the super admin can change it.',
  },
];

// the money overview: the books check, the system accounts, and the economy settings
export function MoneyOverviewPage() {
  const { admin, can } = useAdmin();
  const summary = useRequest(useCallback(() => api.admin.economy.summary(), []));
  const current = useRequest(useCallback(() => api.admin.economy.settings(), []));
  const [form, setForm] = useState<Record<string, string> | null>(null);
  const [holdDays, setHoldDays] = useState('');
  // the fee as a percentage of what is sent (stored as basis points: 1% = 100)
  const [feePercent, setFeePercent] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    const settings = current.data?.settings;
    if (!settings) return;
    const values: Record<string, string> = {};
    for (const key of Object.keys(STARTING_SITUATIONS) as StartingSituation[])
      values[key] = poundsText(settings.startingMoney[key]);
    for (const { key } of LIMITS) values[key] = poundsText(settings[key]);
    setForm(values);
    setHoldDays(String(settings.newAccountHoldDays));
    setFeePercent(String(settings.transferFeeBps / 100));
  }, [current.data]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setSaved(false);
    const pence = (key: string) => parsePounds(form[key] ?? '');
    const candidate = {
      startingMoney: Object.fromEntries(Object.keys(STARTING_SITUATIONS).map((key) => [key, pence(key)])),
      newAccountHoldDays: Number(holdDays),
      transferFeeBps: Math.round(Number(feePercent) * 100),
      ...Object.fromEntries(LIMITS.map(({ key }) => [key, pence(key)])),
    };
    const parsed = EconomySettings.safeParse(candidate);
    if (!parsed.success) return current.fail(new Error('Enter every amount in pounds, like 1500 or 12.50.'));
    try {
      await api.admin.economy.saveSettings(parsed.data);
      await current.reload();
      setSaved(true);
    } catch (problem) {
      current.fail(problem);
    }
  };
  const field = (key: string, label: string, hint: string, disabled = false) => (
    <div className="admin-field" key={key}>
      <Label htmlFor={`economy-${key}`}>{label} (£)</Label>
      <Input
        id={`economy-${key}`}
        inputMode="decimal"
        disabled={disabled}
        value={form?.[key] ?? ''}
        onChange={(event) => {
          setSaved(false);
          setForm({ ...form, [key]: event.target.value });
        }}
      />
      <p className="admin-muted">{hint}</p>
    </div>
  );
  const data = summary.data;
  const editable = can('economy.write');
  return (
    <section>
      <PageHeader
        title="Money"
        description="Every pound in London Life, in one ledger that can only be added to. Checked by the server every hour."
      />
      {summary.error && <ErrorNotice error={summary.error} onRetry={() => void summary.reload()} />}
      {data && (
        <>
          <div className="admin-stats">
            <StatCard label="Held by players" value={formatPence(data.circulationPence)} hint="All lives' accounts" />
            <StatCard label="Transactions" value={data.transactions.toLocaleString('en-GB')} />
            <StatCard
              label="Waiting for approval"
              value={data.pendingApprovals}
              hint={data.pendingApprovals ? 'See Approvals' : 'Nothing waiting'}
            />
            <StatCard
              label="Transfers to check"
              value={data.heldTransfers ?? 0}
              hint={data.heldTransfers ? 'See Transfers' : 'Nothing waiting'}
            />
            <StatCard
              label="Books check"
              value={data.check.ok ? <Badge variant="secondary">Balanced</Badge> : <Badge>Problem found</Badge>}
              hint={data.check.ok ? 'Balances match their history' : data.check.problems[0]}
            />
          </div>
          <div className="admin-panel">
            <h2>System accounts</h2>
            <p className="admin-muted">
              Money enters and leaves the game only through these. A negative balance is money paid out to players.
            </p>
            <DataTable
              rows={data.systemAccounts}
              rowKey={(account) => account.id}
              columns={[
                { key: 'label', header: 'Account', render: (account) => account.label },
                { key: 'balance', header: 'Balance', render: (account) => formatPence(account.balancePence) },
                {
                  key: 'history',
                  header: '',
                  className: 'text-right',
                  render: (account) => (
                    <Link className="admin-muted" to={`/money/players?account=${account.id}`}>
                      History
                    </Link>
                  ),
                },
              ]}
            />
          </div>
        </>
      )}
      {current.error && <ErrorNotice error={current.error} onRetry={() => void current.reload()} />}
      {form && (
        <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)}>
          <h2>Starting money</h2>
          <p className="admin-muted">Paid once to each new life, after the character is created.</p>
          {(Object.entries(STARTING_SITUATIONS) as [StartingSituation, string][]).map(([key, label]) =>
            field(key, label, 'For a new life with this situation.', !editable),
          )}
          <h2>Transfers and approvals</h2>
          <div className="admin-field">
            <Label htmlFor="economy-hold">New account wait (days)</Label>
            <Input
              id="economy-hold"
              inputMode="numeric"
              disabled={!editable}
              value={holdDays}
              onChange={(event) => setHoldDays(event.target.value)}
            />
            <p className="admin-muted">How long a new account waits before it can send money to other players.</p>
          </div>
          <div className="admin-field">
            <Label htmlFor="economy-fee">Fee on transfers (%)</Label>
            <Input
              id="economy-fee"
              inputMode="decimal"
              disabled={!editable}
              value={feePercent}
              onChange={(event) => {
                setSaved(false);
                setFeePercent(event.target.value);
              }}
            />
            <p className="admin-muted">A share of each transfer between players, from 0 to 20%. 0 means no fee.</p>
          </div>
          {LIMITS.map(({ key, label, hint }) =>
            field(key, label, hint, !editable || (key === 'approvalThresholdPence' && !admin.superAdmin)),
          )}
          {editable && (
            <div className="admin-page-actions">
              <Button type="submit">Save money settings</Button>
              {saved && (
                <span role="status" className="admin-muted">
                  Saved and recorded in the audit log.
                </span>
              )}
            </div>
          )}
        </form>
      )}
    </section>
  );
}
